// Ref: https://github.com/deepbluedot/syncly-client-react-vite/blob/main/src/features/WordCloud/components/WordCloud.tsx
import type { FinalWordData } from '@isoterik/react-word-cloud';
import { WordCloud as ReactWordCloud } from '@isoterik/react-word-cloud';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/shared/utils/cn';
import { RingSpinner } from '../loader/ring-spinner';

interface WordCloudItem {
  text: string;
  value: number;
}

interface Props {
  words: WordCloudItem[];
  onWordClick?: (word: FinalWordData, index: number, event: React.MouseEvent) => void;
  className?: string;
  isLoading?: boolean;
  wordCloudId?: string;
  prefix?: string;
}

// Helper function to scale word cloud values
function scaleWordCloudValues(
  words: WordCloudItem[],
  minScale: number,
  maxScale: number,
  prefix?: string,
): WordCloudItem[] {
  if (words.length === 0) return [];

  const values = words.map((w) => w.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  if (minValue === maxValue) {
    return words.map((word) => ({ ...word, value: (minScale + maxScale) / 2 }));
  }

  return words.map((word) => ({
    ...word,
    text: prefix ? `${prefix}${word.text}` : word.text,
    value: minScale + ((word.value - minValue) / (maxValue - minValue)) * (maxScale - minScale),
  }));
}

export function WordCloud({
  words,
  className,
  onWordClick,
  isLoading = false,
  wordCloudId,
  prefix,
}: Props) {
  const { wordCloudSize, setRef } = useResponsiveWordCloud();
  const id = wordCloudId || `wordcloud-${Math.random().toString(36).substring(2, 9)}`;

  const minScale = Math.floor((wordCloudSize.width * wordCloudSize.height) / 750);
  const maxScale = (wordCloudSize.width * wordCloudSize.height) / 100;

  const scaledWords = useMemo(
    () => scaleWordCloudValues(words, minScale, maxScale, prefix),
    [words, minScale, maxScale, prefix],
  );

  const wordCloudKey = useMemo(() => JSON.stringify(scaledWords), [scaledWords]);

  const scaledPadding = 5;

  const getColor = (value: number) => {
    // 상위 30%
    if (value > maxScale * 0.7) {
      return 'var(--color-zinc-800)';
    }

    // 상위 60%
    if (value > maxScale * 0.4) {
      return 'var(--color-zinc-800)'; // zinc-600
    }

    // 상위 80%
    if (value > maxScale * 0.2) {
      return 'var(--color-zinc-500)'; // zinc-400
    }

    return 'var(--color-zinc-300)'; // zinc-300
  };

  return (
    <div
      className={cn('relative h-full overflow-hidden', className)}
      ref={setRef}
      data-wordcloud-id={id}
    >
      <div className="flex h-full w-full items-center justify-center">
        {isLoading ? (
          <div className="flex items-center justify-center">
            <RingSpinner />
          </div>
        ) : scaledWords.length === 0 ? (
          <div className="flex flex-col items-center justify-center space-y-2 text-center">
            <p className="text-muted-foreground text-lg font-medium">No data available</p>
            <p className="text-muted-foreground text-sm">
              There is currently no data available to generate a word cloud.
            </p>
          </div>
        ) : wordCloudSize.width > 0 && wordCloudSize.height > 0 ? (
          <ReactWordCloud
            key={wordCloudKey}
            words={scaledWords}
            width={Math.max(wordCloudSize.width, 400)}
            height={Math.max(wordCloudSize.height, 300)}
            rotate={() => 0}
            padding={scaledPadding}
            font="Instrument Sans"
            fontWeight={600}
            spiral="archimedean"
            fill={(word: FinalWordData) => {
              return getColor(word.value);
            }}
            onWordClick={(
              word: {
                text: string;
                value: number;
              },
              index,
              event,
            ) => {
              onWordClick?.(
                {
                  ...word,
                  text: word.text.replace(prefix ?? '', ''),
                },
                index,
                event,
              );
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center space-y-2 text-center">
            <p className="text-muted-foreground text-lg font-medium">
              Waiting for container size...
            </p>
            <p className="text-muted-foreground text-sm">
              WordCloud container: {wordCloudSize.width}x{wordCloudSize.height}
            </p>
          </div>
        )}
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
              [data-wordcloud-id="${id}"] text {
                paint-order: stroke fill;
                cursor: pointer;
                transition: all 0.2s;
                stroke-width: 0.15em;
                stroke: transparent;
              }
              [data-wordcloud-id="${id}"] text:hover {
                fill: hsl(var(--primary)) !important;
                stroke: var(--color-rebranding-600);
              }
            `,
        }}
      />
    </div>
  );
}

const useResponsiveWordCloud = () => {
  const [wordCloudSize, setWordCloudSize] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateSize = useCallback(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setWordCloudSize({ width, height: height - 2 });
    }
  }, []);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      // 기존 observer 정리
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      if (el) {
        containerRef.current = el;

        // 초기 크기 설정 - requestAnimationFrame으로 지연
        const setInitialSize = () => {
          const { width, height } = el.getBoundingClientRect();
          setWordCloudSize({ width, height: height - 2 });
        };

        // 즉시 설정
        setInitialSize();

        // 다음 프레임에서도 설정 (레이아웃 완료 후)
        requestAnimationFrame(() => {
          setInitialSize();
        });

        // ResizeObserver 설정
        const observer = new ResizeObserver(() => {
          updateSize();
        });

        resizeObserverRef.current = observer;
        observer.observe(el);
      } else {
        containerRef.current = null;
      }
    },
    [updateSize],
  );

  // 컨테이너 크기가 0일 때 재시도하는 useEffect
  useEffect(() => {
    if (wordCloudSize.width === 0 || wordCloudSize.height === 0) {
      const timer = setTimeout(() => {
        if (containerRef.current) {
          const { width, height } = containerRef.current.getBoundingClientRect();
          if (width > 0 && height > 0) {
            setWordCloudSize({ width, height: height - 2 });
          }
        }
      }, 100); // 100ms 후 재시도

      return () => clearTimeout(timer);
    }
  }, [wordCloudSize.width, wordCloudSize.height]);

  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 observer 정리
      const observer = resizeObserverRef.current;
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  return {
    wordCloudSize,
    setRef,
  };
};
